import { types, type IAnyType, type IUnionType, type Instance, type SnapshotOut } from "@gadgetinc/mobx-quick-tree";
import type { Connection, ConnectionConfiguration } from "./Connection";
import { ShopifyAdminAppConfig } from "./shopify/ShopifyAdminAppConfig";
import { ShopifyAdminConnectionConfig } from "./shopify/ShopifyAdminConnectionConfig";
import { ShopifyLegacyAdminAppConfig } from "./shopify/ShopifyLegacyAdminAppConfig";
import { ShopifyLegacyAdminConnectionConfig } from "./shopify/ShopifyLegacyAdminConnectionConfig";
import { ShopifyLegacyPartnerAppConfig } from "./shopify/ShopifyLegacyPartnerAppConfig";
import { ShopifyLegacyPartnerConnectionConfig } from "./shopify/ShopifyLegacyPartnerConnectionConfig";
import { ShopifyPartnerAppConfig } from "./shopify/ShopifyPartnerAppConfig";
import { ShopifyPartnerConnectionConfig } from "./shopify/ShopifyPartnerConnectionConfig";

/**
 * This module contains all the types related to Shopify connections
 *
 * Shopify connection configurations are defined by the type of shopify app they are connecting to
 * The two possible types are:
 * - Partner: these are apps that are created using the Shopify Partners dashboard (or CLI)
 * - Admin: these are apps that are created custom for a single shopify store and are administered through the Shopify Admin
 *
 * In addition to the type of app the connection represents, we also have distinguish between connections that are
 * part of legacy Gadget apps that only support a single development environment and current Gadget apps that support
 * multiple development environments.
 *
 * Based on these two factors, we have 4 different types of Shopify connection configurations:
 * - ShopifyPartnerConnectionConfig: a connection to a Shopify Partner app that supports multiple development environments
 * - ShopifyAdminConnectionConfig: a connection to a Shopify Admin app that supports multiple development environments
 * - ShopifyLegacyPartnerConnectionConfig: a connection to a Shopify Partner app that only supports a single development environment
 * - ShopifyLegacyAdminConnectionConfig: a connection to a Shopify Admin app that only supports a single development environment
 */

/**
 * A union of all valid types of Shopify admin connection configurations
 */
export const ShopifyAnyAdminConnectionConfig = types.union(
  { discriminator: "type" },
  ShopifyLegacyAdminConnectionConfig,
  ShopifyAdminConnectionConfig
);

/**
 * A union of all valid types of Shopify partner connection configurations
 */
export const ShopifyAnyPartnerConnectionConfig = types.union(
  { discriminator: "type" },
  ShopifyLegacyPartnerConnectionConfig,
  ShopifyPartnerConnectionConfig
);

/**
 * A union of all valid types of Shopify connection configurations that support a single development environment
 */

export const SingleDevEnvShopifyConnectionConfig = types.union(
  { discriminator: "type" },
  ShopifyLegacyAdminConnectionConfig,
  ShopifyLegacyPartnerConnectionConfig
);

/**
 * A union of all valid types of Shopify connection configurations that support multiple development environments
 */

export const MultiDevEnvShopifyConnectionConfig = types.union(
  { discriminator: "type" },
  ShopifyPartnerConnectionConfig,
  ShopifyAdminConnectionConfig
);

/**
 * A union of all valid types of Shopify connection configurations
 */
export const ShopifyConnectionConfig = types.union(
  { discriminator: "type" },
  ShopifyAnyAdminConnectionConfig,
  ShopifyAnyPartnerConnectionConfig
);

/**
 * A union of all valid types of Shopify Partner App configurations
 */
export const ShopifyAnyPartnerAppConfig = types.union({ discriminator: "type" }, ShopifyLegacyPartnerAppConfig, ShopifyPartnerAppConfig);

/**
 * A union of all valid types of Shopify Admin App configurations
 */
export const ShopifyAnyAdminAppConfig = types.union({ discriminator: "type" }, ShopifyLegacyAdminAppConfig, ShopifyAdminAppConfig);

export const SingleDevEnvShopifyAppConfig = types.union(
  { discriminator: "type" },
  ShopifyLegacyAdminAppConfig,
  ShopifyLegacyPartnerAppConfig
);

/**
 * A union of all valid types of Shopify app configurations
 */
export const ShopifyAppConfig = types.union({ discriminator: "type" }, ShopifyAnyPartnerAppConfig, ShopifyAnyAdminAppConfig);

type FlattenUnionTypes<T> = T extends IUnionType<infer Types>
  ? FlattenUnionTypes<Types>
  : T extends IAnyType[]
  ? FlattenUnionTypes<T[number]>
  : T;

export type AnyShopifyAppConfig = FlattenUnionTypes<typeof ShopifyAppConfig>;

/**
 * A collection of type guards for Shopify connection configurations and Shopify App configurations
 */

export const isShopifyAdminAppConfig = (
  appConfig: Instance<typeof ShopifyAppConfig>
): appConfig is Instance<typeof ShopifyAnyAdminAppConfig> => {
  return ShopifyAnyAdminAppConfig.is(appConfig);
};

export const isShopifyAdminConnectionConfig = (
  config: Instance<typeof ConnectionConfiguration>
): config is Instance<typeof ShopifyAnyAdminConnectionConfig> => {
  return ShopifyAnyAdminConnectionConfig.is(config);
};

export const isShopifyAdminConnection = (
  connection: Connection
): connection is Connection & { configuration: Instance<typeof ShopifyAnyAdminConnectionConfig> } => {
  return ShopifyAnyAdminConnectionConfig.is(connection.configuration);
};

export const isShopifyPartnerAppConfig = (
  appConfig: Instance<typeof ShopifyAppConfig>
): appConfig is Instance<typeof ShopifyAnyPartnerAppConfig> => {
  return ShopifyAnyPartnerAppConfig.is(appConfig);
};

export const isShopifyPartnerConnectionConfig = (
  config: Instance<typeof ConnectionConfiguration>
): config is Instance<typeof ShopifyAnyPartnerConnectionConfig> => {
  return ShopifyAnyPartnerConnectionConfig.is(config);
};

export const isShopifyPartnerConnection = (
  connection: Connection
): connection is Connection & { configuration: Instance<typeof ShopifyAnyPartnerConnectionConfig> } => {
  return ShopifyAnyPartnerConnectionConfig.is(connection.configuration);
};

export const isShopifyConnectionConfig = (
  config: Instance<typeof ConnectionConfiguration>
): config is Instance<typeof ShopifyConnectionConfig> => {
  return ShopifyConnectionConfig.is(config);
};

export const isShopifyConnectionConfigSnapshot = (
  snapshot: SnapshotOut<typeof ConnectionConfiguration>
): snapshot is SnapshotOut<typeof ShopifyConnectionConfig> => {
  return (
    snapshot.type === "ShopifyManualConnectionConfig" ||
    snapshot.type === "ShopifyConfig" ||
    snapshot.type === "ShopifyPartnerConnectionConfig" ||
    snapshot.type === "ShopifyAdminConnectionConfig"
  );
};

export const isShopifyConnectionSnapshot = (
  snapshot: SnapshotOut<typeof Connection>
): snapshot is SnapshotOut<typeof Connection> & { configuration: SnapshotOut<typeof ShopifyConnectionConfig> } => {
  return isShopifyConnectionConfigSnapshot(snapshot.configuration);
};

export const isShopifyConnection = (
  connection: Connection
): connection is Connection & { configuration: Instance<typeof ShopifyConnectionConfig> } => {
  return ShopifyConnectionConfig.is(connection.configuration);
};
